import type { AlpineComponent, Bindings } from 'alpinejs'
import type { SortDirection, SortEvent } from './table_sort'
import type { ChangeItemCountEvent } from './pagination'

export type SearchCompletedEvent = CustomEvent<{ formId: string }>

// Use on search forms for AJAX updates on a connected `<output>` element and for automatic dirty tracking
// Also handles sorting events triggered with `tableSort` from the connected `<output>` element
export const searchForm = (
  itemsPerPage: number
): AlpineComponent<{
  dirty: boolean
  form: HTMLFormElement
  itemsPerPage: number
  targetElement: HTMLElement
  sortBy: string | null
  sortDirection: SortDirection | null
  submit: () => void
  DebounceInput: Bindings<{ ['@input.debounce'](): void }>
  ChangeInput: Bindings<{ ['@change'](): void }>
}> => ({
  dirty: false,
  form: undefined as unknown as HTMLFormElement,
  itemsPerPage,
  targetElement: undefined as unknown as HTMLOutputElement,
  sortBy: null,
  sortDirection: null,

  init() {
    this.form = this.$root as HTMLFormElement
    this.targetElement = document.querySelector(`output[for="${this.form.id}"]`) as HTMLOutputElement

    if (!this.targetElement) {
      console.error('Output element missing for form id', this.form.id)
      return
    }

    // Stop regular form submissions and replace them with the custom `submit()` method
    // This is useful when you want to initialize the data from outside the component, for example
    this.form.addEventListener('submit', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.submit()
    })

    // Dirty tracking
    this.form.addEventListener('input', () => (this.dirty = true))
    this.form.addEventListener('reset', () => {
      // Submit the form after the inputs have been fully reset to get new, unfiltered search results
      setTimeout(() => this.submit(), 0)
      this.dirty = false
    })

    // Set sort parameters when a 'sort' event for the form is captured
    this.form.addEventListener('sort', (event) => {
      const { detail } = event as SortEvent
      this.sortBy = detail.sortBy
      this.sortDirection = detail.sortDirection
      this.submit()
    })

    // Update items per page from external events
    this.form.addEventListener('change-item-count', (event) => {
      const { detail } = event as ChangeItemCountEvent
      this.itemsPerPage = detail.items
      detail.reload && this.submit()
    })
  },

  // Imitate an AJAX form submission, but add in some custom data from outside the form
  async submit() {
    const params = new URLSearchParams(new FormData(this.form) as unknown as Record<string, string>)

    // Add sorting and items per page information on top of the data from the form
    if (this.sortBy && this.sortDirection) {
      params.set('sort_by', this.sortBy)
      params.set('sort_direction', this.sortDirection)
    }

    params.set('items', this.itemsPerPage.toString())

    try {
      const headers = { Accept: 'text/javascript' }
      const res = await fetch(`${this.form.action}?${params.toString()}`, { headers })
      if (!res.ok) throw new Error()

      this.targetElement.innerHTML = await res.text()
      this.$dispatch('search-completed', { formId: this.form.id })
    } catch {
      alert(window.I18n.unknown_error)
    }
  },

  // Use x-bind="DebounceInput" on form inputs to make them trigger a form submit on input after a small delay
  DebounceInput: {
    ['@input.debounce']() {
      this.submit()
    },
  },

  // Similar to DebounceInput, but for elements that should search on change, such as select boxes and radio buttons
  ChangeInput: {
    ['@change']() {
      this.submit()
    },
  },
})
